// Linked Data Viewer - Shows Magnus-Core data linked to PFMEA
// Customer Requirements, DFMEA Items, BOM/PBS, Critical Characteristics
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileText,
    AlertTriangle,
    Search,
    RefreshCw,
    Link2,
    Target,
    BarChart3,
    TrendingUp,
    Zap,
    Box,
    Star,
    Download,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAppContext } from '../contexts/AppContext';
import clsx from 'clsx';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

// Types for linked data
interface CustomerRequirement {
    id: string;
    customerId: string;
    requirementText: string;
    type: 'Performance' | 'Safety' | 'Usability' | 'Durability' | 'Regulatory';
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    metric?: string;
    target?: string;
    linkedProductFeatureIds: string[];
    projectName?: string;
}

interface DfmeaItem {
    id: string;
    function: string;
    failureMode: string;
    effects: string;
    S: number;  // Severity
    O: number;  // Occurrence
    D: number;  // Detection
    RPN: number;
    actionPriority?: 'H' | 'M' | 'L';
    preventionControls: string[];
    detectionControls: string[];
    status: string;
    projectName?: string;
    specialCharacteristic?: 'CC' | 'SC' | 'none';
    requirement?: string;
    itemFunction?: string;
}

interface Customer {
    id: string;
    name: string;
    industry: string;
    tier: string;
}

interface BomItem {
    componentId: number;
    componentName: string;
    criticality?: 'Critical' | 'High' | 'Medium' | 'Low';
    partNumber?: string;
    description?: string;
    parentComponentId?: number | null;
    quantity?: number;
    type?: string;
    level?: number;
    pbsNodeId?: number;
    specialCharacteristic?: string;  // CC, SC, etc.
}

interface PbsItem {
    pbsNodeId: number;
    name: string;
    partNumber?: string;
    description?: string;
    type?: string;
    level?: number;
    parentPbsNodeId?: number | null;
}

interface Product {
    id: string;
    name: string;
    description?: string;
    features?: { id: string; name: string; description?: string }[];
}

interface LinkedProjectData {
    customers: Customer[];
    customerRequirements: CustomerRequirement[];
    fmeaItems: DfmeaItem[];
    bomItems: BomItem[];
    pbsItems: PbsItem[];
    products: Product[];
    projectName: string;
    fmeaStandard: string;
}

// RPN Risk Level colors
const getRpnColor = (rpn: number) => {
    if (rpn >= 200) return { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Critical' };
    if (rpn >= 100) return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'High' };
    if (rpn >= 40) return { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', label: 'Medium' };
    return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Low' };
};

// Priority colors
const PRIORITY_COLORS: Record<string, string> = {
    'Critical': 'bg-red-500 text-white',
    'High': 'bg-amber-500 text-white',
    'Medium': 'bg-yellow-500 text-slate-800',
    'Low': 'bg-green-500 text-white',
    'H': 'bg-red-500 text-white',
    'M': 'bg-amber-500 text-white',
    'L': 'bg-green-500 text-white',
};

// Helper functions for DFMEA filtering (Complexity Reduction)
const checkScFilter = (item: any, searchQuery: string) => {
    if (!searchQuery.startsWith('sc:')) return true;
    const scFilter = searchQuery.slice(3); // 'CC' or 'SC'
    if (scFilter === 'SC' && item.specialCharacteristic !== 'SC') return false;
    if (scFilter === 'CC' && item.specialCharacteristic !== 'CC') return false;
    return true;
};

const checkTextSearch = (item: any, searchQuery: string) => {
    if (searchQuery.startsWith('sc:') || !searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
        item.function?.toLowerCase().includes(q) ||
        item.failureMode?.toLowerCase().includes(q) ||
        item.effects?.toLowerCase().includes(q)
    );
};

const getCalculatedRpn = (item: any) => item.RPN || ((item.S || 1) * (item.O || 1) * (item.D || 1));

const checkRpnFilter = (item: any, rpnFilter: string) => {
    if (rpnFilter === 'all') return true;
    const rpn = getCalculatedRpn(item);
    if (rpnFilter === 'critical') return rpn >= 200;
    if (rpnFilter === 'high') return rpn >= 100 && rpn < 200;
    if (rpnFilter === 'medium') return rpn >= 40 && rpn < 100;
    if (rpnFilter === 'low') return rpn < 40;
    return true;
};

const LinkedDataPage: React.FC = () => {
    const { theme } = useTheme();
    const { activeProject, activeProjectName } = useAppContext();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // State
    const [linkedData, setLinkedData] = useState<LinkedProjectData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'requirements' | 'bom' | 'dfmea' | 'analysis'>('requirements');
    const [searchQuery, setSearchQuery] = useState('');
    const [rpnFilter, setRpnFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
    // expandedBomIds reserved for future tree view

    // Fetch linked data when project changes
    useEffect(() => {
        // Use activeProjectName (internal name) for API calls
        if (activeProjectName) {
            loadLinkedData(activeProjectName);
        }
    }, [activeProjectName]);

    const loadLinkedData = async (projectName: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await authenticatedFetchJSON<any>(`/api/projects/load/${encodeURIComponent(projectName)}`);

            setLinkedData({
                customers: data.customers || [],
                customerRequirements: data.customerRequirements || [],
                fmeaItems: data.fmeaItems || [],
                bomItems: data.bomItems || [],
                pbsItems: data.pbsItems || [],
                products: data.products || [],
                projectName: data.projectMetadata?.name || projectName,
                fmeaStandard: data.projectMetadata?.fmeaStandard || 'AIAG_VDA',
            });
        } catch (err: any) {
            setError(err.message);
            console.error('Error loading linked data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Helper function for DFMEA filtering (Complexity Reduction)
    const filterDfmeaItem = useCallback((item: any) => {
        if (!checkScFilter(item, searchQuery)) return false;
        if (!checkTextSearch(item, searchQuery)) return false;
        if (!checkRpnFilter(item, rpnFilter)) return false;
        return true;
    }, [searchQuery, rpnFilter]);

    // Filtered DFMEA items
    const filteredDfmeaItems = useMemo(() => {
        if (!linkedData) return [];
        return linkedData.fmeaItems.filter(filterDfmeaItem);
    }, [linkedData, filterDfmeaItem]);

    // Analysis metrics
    const analysisMetrics = useMemo(() => {
        if (!linkedData || linkedData.fmeaItems.length === 0) return null;

        const items = linkedData.fmeaItems;
        const totalRPN = items.reduce((sum, i) => sum + (i.RPN || i.S * i.O * i.D), 0);
        const avgRPN = Math.round(totalRPN / items.length);

        // Severity × Occurrence (S×O) analysis
        const sxoItems = items.map(i => ({ ...i, SxO: i.S * i.O })).sort((a, b) => b.SxO - a.SxO);

        // Severity × Detection (S×D) analysis
        const sxdItems = items.map(i => ({ ...i, SxD: i.S * i.D })).sort((a, b) => b.SxD - a.SxD);

        // Critical items (S >= 9)
        const criticalItems = items.filter(i => i.S >= 9);

        // High RPN items
        const highRpnItems = items.filter(i => (i.RPN || i.S * i.O * i.D) >= 100);

        return {
            totalItems: items.length,
            totalRPN,
            avgRPN,
            maxRPN: Math.max(...items.map(i => i.RPN || i.S * i.O * i.D)),
            criticalCount: criticalItems.length,
            highRpnCount: highRpnItems.length,
            topSxO: sxoItems.slice(0, 5),
            topSxD: sxdItems.slice(0, 5),
            severityDistribution: {
                high: items.filter(i => i.S >= 8).length,
                medium: items.filter(i => i.S >= 5 && i.S < 8).length,
                low: items.filter(i => i.S < 5).length,
            }
        };
    }, [linkedData]);

    if (!activeProject || activeProject === 'Select Project') {
        return (
            <div className={clsx('h-full flex items-center justify-center', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
                <div className="text-center max-w-md">
                    <Link2 size={48} className="mx-auto mb-4 text-slate-400" />
                    <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                        No Project Selected
                    </h2>
                    <p className="text-slate-500 mb-4">
                        Select a project from the Project Management page to view linked DFMEA data.
                    </p>
                    <button
                        onClick={() => navigate('/projects')}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                        Go to Projects
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={clsx('h-full flex flex-col', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
            {/* Header */}
            <header className={clsx(
                'h-16 border-b flex items-center justify-between px-6 shrink-0',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-4">
                    <Link2 size={28} className="text-primary-500" />
                    <div>
                        <h1 className={clsx('text-xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                            Linked Data Explorer
                        </h1>
                        <p className="text-xs text-slate-500">
                            Customer Requirements, DFMEA, and Risk Analysis from {activeProject}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => activeProjectName && loadLinkedData(activeProjectName)}
                        disabled={loading}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        )}
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </header>

            {/* Tabs */}
            <div className={clsx(
                'border-b flex items-center gap-4 px-6',
                isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
            )}>
                {[
                    { id: 'requirements', label: 'Customer Requirements', icon: FileText },
                    { id: 'bom', label: 'BOM & PBS', icon: Box },
                    { id: 'dfmea', label: 'DFMEA Items', icon: AlertTriangle },
                    { id: 'analysis', label: 'Risk Analysis', icon: BarChart3 },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as typeof activeTab)}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                            activeTab === tab.id
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                        )}
                    >
                        <tab.icon size={16} />
                        {tab.label}
                        {linkedData && (
                            <span className={clsx(
                                'ml-1 px-2 py-0.5 text-xs rounded-full',
                                activeTab === tab.id ? 'bg-primary-100 dark:bg-primary-900/50' : 'bg-slate-100 dark:bg-slate-700'
                            )}>
                                {tab.id === 'requirements' ? linkedData.customerRequirements.length :
                                    tab.id === 'bom' ? linkedData.bomItems.length :
                                        tab.id === 'dfmea' ? linkedData.fmeaItems.length : ''}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw size={32} className="animate-spin text-primary-500" />
                    </div>
                ) : error ? (
                    <div className={clsx(
                        'p-6 rounded-xl border max-w-md mx-auto text-center',
                        isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                    )}>
                        <AlertTriangle size={32} className="mx-auto mb-3 text-red-500" />
                        <p className="text-red-600">{error}</p>
                    </div>
                ) : !linkedData ? (
                    <div className="text-center py-12 text-slate-500">
                        No data loaded yet. Click Refresh to load project data.
                    </div>
                ) : activeTab === 'requirements' ? (
                    /* Customer Requirements Tab */
                    <div className="space-y-4">
                        {linkedData.customerRequirements.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                                No customer requirements linked to this project in DFMEA.
                            </div>
                        ) : (
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {linkedData.customerRequirements.map(req => (
                                    <div
                                        key={req.id}
                                        className={clsx(
                                            'p-4 rounded-xl border',
                                            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                        )}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <span className={clsx(
                                                'px-2 py-0.5 text-xs font-medium rounded-full',
                                                PRIORITY_COLORS[req.priority] || 'bg-slate-200 text-slate-700'
                                            )}>
                                                {req.priority}
                                            </span>
                                            <span className="text-xs text-slate-500">{req.type}</span>
                                        </div>
                                        <p className={clsx(
                                            'text-sm mb-3',
                                            isDark ? 'text-white' : 'text-slate-800'
                                        )}>
                                            {req.requirementText}
                                        </p>
                                        {req.metric && (
                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                <Target size={12} />
                                                {req.metric}: {req.target}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : activeTab === 'bom' ? (
                    /* BOM & PBS Tab */
                    <div className="space-y-4">
                        {linkedData.bomItems.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                                No BOM items linked to this project.
                            </div>
                        ) : (
                            <>
                                {/* Critical Characteristics Summary */}
                                {linkedData.bomItems.filter(b => b.criticality === 'Critical' || b.specialCharacteristic).length > 0 && (
                                    <div className={clsx(
                                        'p-4 rounded-xl border',
                                        isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                                    )}>
                                        <h3 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
                                            <Star size={16} />
                                            Special Characteristics (CC/SC)
                                        </h3>
                                        <div className="flex flex-wrap gap-2">
                                            {linkedData.bomItems
                                                .filter(b => b.criticality === 'Critical' || b.specialCharacteristic)
                                                .map(b => (
                                                    <span
                                                        key={b.componentId}
                                                        className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-full"
                                                    >
                                                        {b.componentName}
                                                        {b.specialCharacteristic && ` (${b.specialCharacteristic})`}
                                                    </span>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {/* BOM Table */}
                                <div className={clsx(
                                    'rounded-xl border overflow-hidden',
                                    isDark ? 'border-slate-700' : 'border-slate-200'
                                )}>
                                    <table className="w-full text-sm">
                                        <thead className={clsx(isDark ? 'bg-slate-800' : 'bg-slate-50')}>
                                            <tr>
                                                <th className="px-4 py-3 text-left font-medium text-slate-500">Component</th>
                                                <th className="px-4 py-3 text-left font-medium text-slate-500">Part Number</th>
                                                <th className="px-4 py-3 text-left font-medium text-slate-500">Type</th>
                                                <th className="px-4 py-3 text-center font-medium text-slate-500">Qty</th>
                                                <th className="px-4 py-3 text-center font-medium text-slate-500">Criticality</th>
                                                <th className="px-4 py-3 text-center font-medium text-slate-500">CC/SC</th>
                                            </tr>
                                        </thead>
                                        <tbody className={isDark ? 'bg-slate-900' : 'bg-white'}>
                                            {linkedData.bomItems.map(item => (
                                                <tr key={item.componentId} className={clsx(
                                                    'border-t',
                                                    isDark ? 'border-slate-700' : 'border-slate-200',
                                                    item.criticality === 'Critical' && (isDark ? 'bg-red-900/10' : 'bg-red-50/50')
                                                )}>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <Box size={14} className="text-slate-400" />
                                                            <span className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                                                                {item.componentName}
                                                            </span>
                                                        </div>
                                                        {item.description && (
                                                            <div className="text-xs text-slate-500 mt-0.5 ml-6">{item.description}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 font-mono text-xs">
                                                        {item.partNumber || '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                                        {item.type || '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-300">
                                                        {item.quantity || 1}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {item.criticality && (
                                                            <span className={clsx(
                                                                'px-2 py-0.5 rounded text-xs font-medium',
                                                                PRIORITY_COLORS[item.criticality]
                                                            )}>
                                                                {item.criticality}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {item.specialCharacteristic && (
                                                            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded text-xs font-bold">
                                                                {item.specialCharacteristic}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Products Section */}
                                {linkedData.products.length > 0 && (
                                    <div className={clsx(
                                        'p-5 rounded-xl border',
                                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                    )}>
                                        <h3 className={clsx(
                                            'text-sm font-semibold mb-3 flex items-center gap-2',
                                            isDark ? 'text-white' : 'text-slate-800'
                                        )}>
                                            <Box size={16} className="text-primary-500" />
                                            Products & Features
                                        </h3>
                                        <div className="grid gap-3 md:grid-cols-2">
                                            {linkedData.products.map(product => (
                                                <div key={product.id} className={clsx(
                                                    'p-3 rounded-lg',
                                                    isDark ? 'bg-slate-700' : 'bg-slate-50'
                                                )}>
                                                    <div className={clsx('font-medium text-sm', isDark ? 'text-white' : 'text-slate-800')}>
                                                        {product.name}
                                                    </div>
                                                    {product.description && (
                                                        <div className="text-xs text-slate-500 mt-1">{product.description}</div>
                                                    )}
                                                    {product.features && product.features.length > 0 && (
                                                        <div className="mt-2 flex flex-wrap gap-1">
                                                            {product.features.map(f => (
                                                                <span key={f.id} className="text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded">
                                                                    {f.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : activeTab === 'dfmea' ? (
                    /* DFMEA Items Tab */
                    <div className="space-y-4">
                        {/* Filters */}
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="relative flex-1 max-w-md">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search failure modes..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className={clsx(
                                        'w-full pl-9 pr-4 py-2 rounded-lg border text-sm',
                                        isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                                    )}
                                />
                            </div>

                            <select
                                value={rpnFilter}
                                onChange={e => setRpnFilter(e.target.value as typeof rpnFilter)}
                                className={clsx(
                                    'px-3 py-2 rounded-lg border text-sm',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                                )}
                            >
                                <option value="all">All RPN Levels</option>
                                <option value="critical">Critical (≥200)</option>
                                <option value="high">High (100-199)</option>
                                <option value="medium">Medium (40-99)</option>
                                <option value="low">Low (&lt;40)</option>
                            </select>

                            {/* SC/CC Quick Filter */}
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 font-medium">SC/CC:</span>
                                {(['all', 'CC', 'SC'] as const).map(sc => (
                                    <button
                                        key={sc}
                                        onClick={() => {
                                            // Use searchQuery to filter by SC/CC prefix
                                            if (sc === 'all') {
                                                setSearchQuery('');
                                            } else {
                                                setSearchQuery(`sc:${sc}`);
                                            }
                                        }}
                                        className={clsx(
                                            'px-2 py-1 rounded text-xs font-bold transition-colors',
                                            searchQuery === `sc:${sc}` || (sc === 'all' && !searchQuery.startsWith('sc:'))
                                                ? sc === 'CC' ? 'bg-red-500 text-white'
                                                    : sc === 'SC' ? 'bg-amber-500 text-white'
                                                        : clsx(isDark ? 'bg-primary-500 text-white' : 'bg-primary-500 text-white')
                                                : clsx(isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')
                                        )}
                                    >
                                        {sc === 'all' ? 'All' : sc}
                                    </button>
                                ))}
                            </div>

                            <span className="text-sm text-slate-500">
                                {filteredDfmeaItems.length} items
                                {linkedData.fmeaItems.filter(i => i.specialCharacteristic && i.specialCharacteristic !== 'none').length > 0 && (
                                    <span className="ml-2 text-amber-500 font-medium">
                                        ({linkedData.fmeaItems.filter(i => i.specialCharacteristic === 'CC').length} CC, {linkedData.fmeaItems.filter(i => i.specialCharacteristic === 'SC').length} SC)
                                    </span>
                                )}
                            </span>

                            {/* Import SC/CC to PFMEA Button */}
                            {linkedData.fmeaItems.filter(i => i.specialCharacteristic && i.specialCharacteristic !== 'none').length > 0 && (
                                <button
                                    onClick={() => {
                                        const scItems = linkedData.fmeaItems.filter(i => i.specialCharacteristic && i.specialCharacteristic !== 'none');
                                        const importData = scItems.map(item => ({
                                            dfmeaId: item.id,
                                            function: item.function,
                                            failureMode: item.failureMode,
                                            effects: item.effects,
                                            severity: item.S,
                                            specialCharacteristic: item.specialCharacteristic,
                                            preventionControls: item.preventionControls,
                                            detectionControls: item.detectionControls,
                                        }));
                                        // Store in sessionStorage for PFMEA editor to pick up
                                        sessionStorage.setItem('dfmea_sc_import', JSON.stringify(importData));
                                        navigate('/editor');
                                    }}
                                    className={clsx(
                                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                        'bg-gradient-to-r from-red-500 to-amber-500 text-white hover:from-red-600 hover:to-amber-600 shadow-sm'
                                    )}
                                >
                                    <Download size={14} />
                                    Import SC/CC to PFMEA
                                </button>
                            )}
                        </div>

                        {/* DFMEA Table */}
                        <div className={clsx(
                            'rounded-xl border overflow-hidden',
                            isDark ? 'border-slate-700' : 'border-slate-200'
                        )}>
                            <table className="w-full text-sm">
                                <thead className={clsx(isDark ? 'bg-slate-800' : 'bg-slate-50')}>
                                    <tr>
                                        <th className="px-4 py-3 text-left font-medium text-slate-500">Function</th>
                                        <th className="px-4 py-3 text-left font-medium text-slate-500">Failure Mode</th>
                                        <th className="px-4 py-3 text-center font-medium text-slate-500">S</th>
                                        <th className="px-4 py-3 text-center font-medium text-slate-500">Class</th>
                                        <th className="px-4 py-3 text-center font-medium text-slate-500">O</th>
                                        <th className="px-4 py-3 text-center font-medium text-slate-500">D</th>
                                        <th className="px-4 py-3 text-center font-medium text-slate-500">RPN</th>
                                        <th className="px-4 py-3 text-center font-medium text-slate-500">S×O</th>
                                        <th className="px-4 py-3 text-center font-medium text-slate-500">AP</th>
                                    </tr>
                                </thead>
                                <tbody className={isDark ? 'bg-slate-900' : 'bg-white'}>
                                    {filteredDfmeaItems.map(item => {
                                        const rpn = item.RPN || (item.S * item.O * item.D);
                                        const sxo = item.S * item.O;
                                        const rpnStyle = getRpnColor(rpn);

                                        return (
                                            <tr key={item.id} className={clsx(
                                                'border-t',
                                                isDark ? 'border-slate-700' : 'border-slate-200'
                                            )}>
                                                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                                    {item.function}
                                                </td>
                                                <td className="px-4 py-3 font-medium text-slate-800 dark:text-white">
                                                    {item.failureMode}
                                                </td>
                                                <td className={clsx(
                                                    'px-4 py-3 text-center font-bold',
                                                    item.S >= 9 ? 'text-red-600' : item.S >= 7 ? 'text-amber-600' : 'text-slate-600'
                                                )}>
                                                    {item.S}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {item.specialCharacteristic === 'CC' ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">CC</span>
                                                    ) : item.specialCharacteristic === 'SC' ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">SC</span>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-300">
                                                    {item.O}
                                                </td>
                                                <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-300">
                                                    {item.D}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={clsx(
                                                        'px-2 py-1 rounded-full text-xs font-bold',
                                                        rpnStyle.bg, rpnStyle.text
                                                    )}>
                                                        {rpn}
                                                    </span>
                                                </td>
                                                <td className={clsx(
                                                    'px-4 py-3 text-center font-medium',
                                                    sxo >= 36 ? 'text-red-600' : sxo >= 16 ? 'text-amber-600' : 'text-slate-600'
                                                )}>
                                                    {sxo}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {item.actionPriority && (
                                                        <span className={clsx(
                                                            'px-2 py-0.5 rounded text-xs font-bold',
                                                            PRIORITY_COLORS[item.actionPriority]
                                                        )}>
                                                            {item.actionPriority}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    /* Risk Analysis Tab */
                    <div className="space-y-6">
                        {analysisMetrics ? (
                            <>
                                {/* Summary Cards */}
                                <div className="grid gap-4 md:grid-cols-4">
                                    <div className={clsx(
                                        'p-4 rounded-xl border',
                                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                    )}>
                                        <div className="text-sm text-slate-500 mb-1">Total DFMEA Items</div>
                                        <div className="text-2xl font-bold text-primary-500">{analysisMetrics.totalItems}</div>
                                    </div>
                                    <div className={clsx(
                                        'p-4 rounded-xl border',
                                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                    )}>
                                        <div className="text-sm text-slate-500 mb-1">Average RPN</div>
                                        <div className="text-2xl font-bold text-amber-500">{analysisMetrics.avgRPN}</div>
                                    </div>
                                    <div className={clsx(
                                        'p-4 rounded-xl border',
                                        isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                                    )}>
                                        <div className="text-sm text-red-600 mb-1">Critical (S≥9)</div>
                                        <div className="text-2xl font-bold text-red-600">{analysisMetrics.criticalCount}</div>
                                    </div>
                                    <div className={clsx(
                                        'p-4 rounded-xl border',
                                        isDark ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'
                                    )}>
                                        <div className="text-sm text-amber-600 mb-1">High RPN (≥100)</div>
                                        <div className="text-2xl font-bold text-amber-600">{analysisMetrics.highRpnCount}</div>
                                    </div>
                                </div>

                                {/* Top S×O Items */}
                                <div className={clsx(
                                    'p-5 rounded-xl border',
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                )}>
                                    <h3 className={clsx(
                                        'text-lg font-semibold mb-4 flex items-center gap-2',
                                        isDark ? 'text-white' : 'text-slate-800'
                                    )}>
                                        <Zap size={20} className="text-amber-500" />
                                        Top 5 by Severity × Occurrence (S×O)
                                    </h3>
                                    <div className="space-y-3">
                                        {analysisMetrics.topSxO.map((item, idx) => (
                                            <div key={item.id} className="flex items-center gap-4">
                                                <span className={clsx(
                                                    'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                                                    idx === 0 ? 'bg-red-500 text-white' :
                                                        idx === 1 ? 'bg-amber-500 text-white' :
                                                            'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                                )}>
                                                    {idx + 1}
                                                </span>
                                                <div className="flex-1">
                                                    <div className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                                                        {item.failureMode}
                                                    </div>
                                                    <div className="text-xs text-slate-500">{item.function}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-bold text-amber-600">S×O: {(item as any).SxO}</div>
                                                    <div className="text-xs text-slate-500">S={item.S} × O={item.O}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Top S×D Items */}
                                <div className={clsx(
                                    'p-5 rounded-xl border',
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                )}>
                                    <h3 className={clsx(
                                        'text-lg font-semibold mb-4 flex items-center gap-2',
                                        isDark ? 'text-white' : 'text-slate-800'
                                    )}>
                                        <TrendingUp size={20} className="text-purple-500" />
                                        Top 5 by Severity × Detection (S×D) - Detection Risk
                                    </h3>
                                    <div className="space-y-3">
                                        {analysisMetrics.topSxD.map((item, idx) => (
                                            <div key={item.id} className="flex items-center gap-4">
                                                <span className={clsx(
                                                    'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                                                    idx === 0 ? 'bg-purple-500 text-white' :
                                                        idx === 1 ? 'bg-purple-400 text-white' :
                                                            'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                                )}>
                                                    {idx + 1}
                                                </span>
                                                <div className="flex-1">
                                                    <div className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                                                        {item.failureMode}
                                                    </div>
                                                    <div className="text-xs text-slate-500">{item.function}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-bold text-purple-600">S×D: {(item as any).SxD}</div>
                                                    <div className="text-xs text-slate-500">S={item.S} × D={item.D}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12 text-slate-500">
                                No DFMEA data available for analysis.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <footer className={clsx(
                'h-10 border-t flex items-center justify-between px-6 text-xs',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-2 text-slate-500">
                    <Link2 size={14} className="text-green-500" />
                    Linked to Magnus-Core DFMEA data
                </div>
                {linkedData && (
                    <div className="text-slate-500">
                        Standard: <span className="font-medium text-primary-500">{linkedData.fmeaStandard}</span>
                    </div>
                )}
            </footer>
        </div>
    );
};

export default LinkedDataPage;
